import React, { useState, useEffect, useMemo, useRef } from "react";
import { Form, Button, Table, Overlay, Popover, ListGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { debounce } from "lodash";
import arrowLeft from "../../assets/arrow-left.svg";
import deleteIcon from "../../assets/delete.svg";
import editIcon from "../../assets/edit.svg";
import productRoute from "../../services/productRoute";
import { useAppContext } from "../../AppContext";
import { useNavigate, useParams } from "react-router-dom";
import InstructionModal from "../../components/Modal/InstructionModal";

const ShoppingInstructionDetails = () => {
  const { showToast } = useAppContext();
  const navigate = useNavigate();
  const { instructionId } = useParams();
  const { getSingleShoppingInstruction, updateShoppingInstruction, getGlobalProducts } =
    productRoute();

  const [loading, setLoading] = useState(true);
  const [instruction, setInstruction] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [showProducts, setShowProducts] = useState(false);
  const [globalProducts, setGlobalProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [searchProduct, setSearchProduct] = useState("");
  const [showEditInstruction, setShowEditInstruction] = useState(false);
  const popoverRef = useRef(null);

  const loadInstruction = () => {
    getSingleShoppingInstruction(instructionId, setLoading, showToast, setInstruction);
  };

  useEffect(() => {
    loadInstruction();
  }, [instructionId]);

  useEffect(() => {
    const debouncedGetProducts = debounce((term) => {
      getGlobalProducts(setProductsLoading, showToast, setGlobalProducts, term, 1000);
    }, 500);

    if (searchProduct !== "") {
      debouncedGetProducts(searchProduct);
    } else {
      getGlobalProducts(setProductsLoading, showToast, setGlobalProducts, "", 1000);
    }

    return () => {
      debouncedGetProducts.cancel();
    };
  }, [searchProduct]);

  const attachedProducts = useMemo(() => {
    const raw = instruction?.products || instruction?.productIds || [];
    return raw.map((product) => {
      if (typeof product === "string") {
        const match = (globalProducts || []).find((p) => (p?.id || p?._id) === product);
        return match ? { ...match, id: product } : { id: product, productName: product };
      }
      return { ...product, id: product?.id || product?._id };
    });
  }, [instruction, globalProducts]);

  const attachedIds = attachedProducts.map((product) => product.id);

  const saveProductIds = (productIds, toastText) => {
    const body = {
      productIds,
      name: instruction?.name,
      amount: instruction?.amount,
    };

    updateShoppingInstruction(
      instructionId,
      body,
      setUpdating,
      showToast,
      () => {
        setTogglingId(null);
        loadInstruction();
      },
      toastText
    );
  };

  const handleToggleProduct = (productId) => {
    if (updating) return;
    setTogglingId(productId);

    if (attachedIds.includes(productId)) {
      saveProductIds(
        attachedIds.filter((id) => id !== productId),
        "Product removed from instruction"
      );
    } else {
      saveProductIds([...attachedIds, productId], "Product added to instruction");
    }
  };

  const toggleProductsPopover = () => setShowProducts(!showProducts);

  return (
    <section className="list-page">
      <div className="d-flex justify-content-between align-items-center header-admin py-5 pe-5 ps-4">
        <div className="d-flex align-items-center gap-3">
          <img
            src={arrowLeft}
            alt="back"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/admin/shopping-instructions")}
          />
          <h3 className="mb-0" style={{ color: "#344054" }}>
            {instruction?.name || "Shopping Instruction"}
          </h3>
        </div>
        <div className="d-flex gap-3">
          <Button
            variant="secondary"
            className="d-flex align-items-center gap-2 p-2"
            style={{ fontWeight: "500", color: "#333333" }}
            onClick={() => setShowEditInstruction(true)}
            disabled={loading || !instruction}
          >
            <img src={editIcon} alt="edit" />
            Edit Instruction
          </Button>
          <Button
            className="d-flex align-items-center gap-3 p-2"
            onClick={toggleProductsPopover}
            ref={popoverRef}
            disabled={loading || !instruction}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Products
          </Button>

          <Overlay
            show={showProducts}
            target={popoverRef.current}
            placement="bottom"
            rootClose={true}
            onHide={() => setShowProducts(false)}
          >
            <Popover id="instruction-products-popover" className="custom-popover" style={{ width: "430px" }}>
              <Popover.Body>
                <div className="d-flex align-items-center position-relative py-1 px-1 mb-3">
                  {searchProduct !== "" && (
                    <i
                      className="bi bi-x"
                      style={{
                        top: "5%",
                        right: "1.5%",
                        position: "absolute",
                        fontSize: "1.5rem",
                        color: "#98A2B3",
                      }}
                      onClick={() => setSearchProduct("")}
                    ></i>
                  )}
                  <Form.Control
                    style={{
                      border: "1px solid #E1E6EF",
                      backgroundColor: "#E1E6EF",
                      borderRadius: "3px",
                      fontWeight: "500",
                    }}
                    placeholder="Search by product name"
                    value={searchProduct}
                    onChange={(e) => setSearchProduct(e.target.value)}
                    autoFocus
                  />
                </div>
                {productsLoading ? (
                  <div
                    className="justify-content-center align-items-center text-center"
                    style={{ padding: "150px" }}
                  >
                    <div className="spinner-border text-success" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : globalProducts?.length > 0 ? (
                  <>
                    <div style={{ borderTop: "1px solid #E1E6EF" }} className="pb-2 pt-3">
                      <h5 className="" style={{ fontWeight: "500" }}>
                        Products
                      </h5>
                    </div>
                    <ListGroup
                      style={{ maxHeight: "300px", overflowY: "scroll", border: "none" }}
                      className="no-border-listgroup"
                    >
                      {globalProducts.map((product) => {
                        const productId = product?.id || product?._id;
                        return (
                          <ListGroup.Item
                            key={productId}
                            action
                            className="d-flex justify-content-between align-items-center"
                          >
                            <Form.Check
                              type="checkbox"
                              label={`${product?.productName}${product?.size ? ` - ${product?.size}` : ""}`}
                              checked={attachedIds.includes(productId)}
                              disabled={updating}
                              onChange={() => handleToggleProduct(productId)}
                            />
                            {togglingId === productId && updating && (
                              <div
                                className="spinner-border spinner-border-sm text-success"
                                role="status"
                              >
                                <span className="visually-hidden">Saving...</span>
                              </div>
                            )}
                          </ListGroup.Item>
                        );
                      })}
                    </ListGroup>
                  </>
                ) : searchProduct !== "" ? (
                  <div className="fw-medium" style={{ padding: "50px 0" }}>
                    <h4 className="mb-2">No products found</h4>
                    <Button onClick={() => setSearchProduct("")}>View all products</Button>
                  </div>
                ) : (
                  <div className="fw-medium" style={{ padding: "50px 0" }}>
                    <h4>You have no products</h4>
                  </div>
                )}
              </Popover.Body>
            </Popover>
          </Overlay>
        </div>
      </div>

      {loading ? (
        <div
          className="justify-content-center align-items-center text-center"
          style={{ padding: "100px" }}
        >
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="px-4 list-body">
          <div className="d-flex align-items-center justify-content-between py-3">
            <p style={{ color: "#1D2939", fontWeight: "500" }} className="my-auto">
              {attachedProducts.length} {attachedProducts.length === 1 ? "Product" : "Products"}
            </p>
            {updating && !togglingId && (
              <div className="spinner-border spinner-border-sm text-success" role="status">
                <span className="visually-hidden">Saving...</span>
              </div>
            )}
          </div>

          {attachedProducts.length > 0 ? (
            <div className="list-scroll">
              <Table hover className="my-3">
                <thead>
                  <tr style={{ fontWeight: "600" }} className="mb-2">
                    <th style={{ fontWeight: "600" }} className="pb-3">
                      Name
                    </th>
                    <th style={{ fontWeight: "600" }} className="pb-3">
                      Categories
                    </th>
                    <th style={{ fontWeight: "600" }} className="pb-3">
                      Selling Price
                    </th>
                    <th style={{ fontWeight: "600" }} className="pb-3"></th>
                    <th style={{ fontWeight: "600" }} className="pb-3"></th>
                  </tr>
                </thead>
                <tbody className="py-2">
                  {attachedProducts.map((product) => (
                    <tr
                      style={{ fontSize: "14px", fontWeight: "400", color: "#333333" }}
                      key={product?.id}
                      onClick={() => navigate(`/admin/products/${product?.id}`)}
                    >
                      <td
                        style={{
                          paddingTop: "15px",
                          paddingBottom: "15px",
                          color: "#194298",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        {product?.productName}
                        {product?.size && ` - ${product?.size}`}
                      </td>
                      <td style={{ paddingTop: "15px", paddingBottom: "15px" }}>
                        {product?.categories?.map((category) => category.categoryName).join(", ")}
                      </td>
                      <td style={{ paddingTop: "15px", paddingBottom: "15px" }}>
                        {product?.price ? `₦${product?.price?.toLocaleString()}` : "-"}
                      </td>
                      <td style={{ paddingTop: "15px", paddingBottom: "15px" }}>
                        <div onClick={(e) => e.stopPropagation()} className="d-flex">
                          {togglingId === product?.id && updating ? (
                            <div
                              className="spinner-border spinner-border-sm text-success"
                              role="status"
                            >
                              <span className="visually-hidden">Removing...</span>
                            </div>
                          ) : (
                            <img
                              src={deleteIcon}
                              alt="remove"
                              style={{ cursor: updating ? "not-allowed" : "pointer" }}
                              onClick={() => handleToggleProduct(product?.id)}
                            />
                          )}
                        </div>
                      </td>
                      <td
                        style={{
                          paddingTop: "15px",
                          paddingBottom: "15px",
                          cursor: "pointer",
                          textDecoration: "underline",
                          color: "#469B12",
                        }}
                        onClick={() => navigate(`/admin/products/${product?.id}`)}
                      >
                        View
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="fw-medium" style={{ padding: "40px 0" }}>
              <h4 className="mb-2">No products have this instruction</h4>
              <Button onClick={toggleProductsPopover}>Add Products</Button>
            </div>
          )}
        </div>
      )}

      <InstructionModal
        showInstructionModal={showEditInstruction}
        setShowInstructionModal={setShowEditInstruction}
        instruction={instruction}
        withProducts
        onSaved={loadInstruction}
      />
    </section>
  );
};

export default ShoppingInstructionDetails;
